import {
	CPU_AIM_PROFILES,
	MAX_ANGLE,
	MAX_POWER,
	MIN_ANGLE,
	MIN_POWER,
	TANK_HEIGHT,
	WEAPONS,
	WEAPON_ORDER,
	WORLD_HEIGHT,
	WORLD_WIDTH,
} from './scorched-earth.constants';
import {
	distance,
	launchVelocity,
	projectileHitsTerrain,
	projectileOutOfBounds,
	stepProjectile,
} from './scorched-earth.physics';
import { hashSeed } from './scorched-earth.rng';
import { fireWeapon, passTurn, updateAim } from './scorched-earth.engine';
import {
	AimInput,
	CpuDifficulty,
	Inventory,
	Projectile,
	ScorchedEarthState,
	Tank,
	WeaponId,
} from './scorched-earth.types';

function totalAmmo(inventory: Inventory): number {
	return (
		inventory.babyMissile +
		inventory.missile +
		inventory.babyNuke +
		inventory.nuke +
		inventory.mirv +
		inventory.napalm
	);
}

export type CpuTurnDecision =
	| { action: 'pass' }
	| { action: 'fire'; aim: AimInput };

export function isCpuPlayer(player: Tank | null | undefined): boolean {
	return player?.kind === 'cpu';
}

export function getCpuProfile(difficulty: CpuDifficulty | null | undefined) {
	return CPU_AIM_PROFILES[difficulty ?? 'normal'];
}

export function cpuShouldPass(state: ScorchedEarthState, player: Tank): boolean {
	return totalAmmo(player.inventory) === 0;
}

export function cpuChooseWeapon(state: ScorchedEarthState, player: Tank): WeaponId {
	const profile = getCpuProfile(player.difficulty);
	const enemies = state.players.filter((p) => p.alive && p.id !== player.id);
	if (!enemies.length) {
		return pickFirstAvailable(player);
	}

	const seed = hashSeed([
		state.seed,
		state.round,
		player.id,
		state.currentPlayerIndex,
		enemies.reduce((s, e) => s + e.health, 0),
	]);
	let randCursor = seed;
	const rand = () => seededUnit(randCursor++);
	const target = pickPrimaryTarget(player, enemies);
	const dist = distance({ x: player.x, y: player.y }, { x: target.x, y: target.y });
	const lowHp = target.health <= 35;
	const clustered =
		enemies.filter((e) => distance({ x: target.x, y: target.y }, { x: e.x, y: e.y }) < 80).length >= 2;

	const scores: Partial<Record<WeaponId, number>> = {};
	for (const id of WEAPON_ORDER) {
		if (player.inventory[id] <= 0) {
			continue;
		}
		let score = 1;
		switch (id) {
		case 'babyMissile':
			score = lowHp ? 6 : dist > 220 ? 4 : 3;
			break;
		case 'missile':
			score = dist < 260 ? 5 : 4;
			break;
		case 'babyNuke':
			score = clustered ? 6 : dist > 180 ? 4 : 3;
			break;
		case 'nuke':
			score = clustered && enemies.length >= 2 ? 7 : dist > 140 ? 5 : 2;
			break;
		case 'mirv':
			score = enemies.length >= 2 ? 5 : dist > 200 ? 4 : 2;
			break;
		case 'napalm':
			score = target.health > 50 && dist < 220 ? 5 : 2;
			break;
		}
		scores[id] = score + rand() * profile.weaponPickNoise * 10;
	}

	let best: WeaponId = pickFirstAvailable(player);
	let bestScore = -Infinity;
	for (const id of WEAPON_ORDER) {
		const score = scores[id];
		if (score !== undefined && score > bestScore) {
			bestScore = score;
			best = id;
		}
	}
	return best;
}

export function cpuComputeAim(
	state: ScorchedEarthState,
	player: Tank,
	weaponId: WeaponId,
): { angle: number; power: number } {
	const profile = getCpuProfile(player.difficulty);
	const enemies = state.players.filter((p) => p.alive && p.id !== player.id);
	const target = enemies.length ? pickPrimaryTarget(player, enemies) : player;
	const facingRight = player.x <= target.x;
	const startX = player.x;
	const startY = player.y - TANK_HEIGHT;
	const targetPoint = { x: target.x, y: target.y - TANK_HEIGHT * 0.5 };

	let bestAngle = player.angle;
	let bestPower = player.power;
	let bestErr = Infinity;

	for (let power = MIN_POWER; power <= MAX_POWER; power += 4) {
		for (let angle = MIN_ANGLE; angle <= MAX_ANGLE; angle += 2) {
			const impact = simulateImpact({
				angle,
				power,
				startX,
				startY,
				facingRight,
				weaponId,
				wind: state.wind,
				terrain: state.terrain,
			});
			if (!impact) {
				continue;
			}
			const err = distance(impact, targetPoint);
			if (err < bestErr) {
				bestErr = err;
				bestAngle = angle;
				bestPower = power;
			}
		}
	}

	const noiseSeed = hashSeed([state.seed, state.round, player.id, WEAPON_ORDER.indexOf(weaponId), state.wind]);
	const n1 = seededUnit(noiseSeed) * 2 - 1;
	const n2 = seededUnit(noiseSeed + 17) * 2 - 1;
	return {
		angle: clamp(bestAngle + n1 * profile.angleErrorDeg, MIN_ANGLE, MAX_ANGLE),
		power: clamp(Math.round(bestPower + n2 * profile.powerError), MIN_POWER, MAX_POWER),
	};
}

export function applyCpuTurn(state: ScorchedEarthState): ScorchedEarthState {
	const decision = planCpuTurn(state);
	if (decision.action === 'pass') {
		return passTurn(state);
	}
	let next = updateAim(state, decision.aim);
	next = fireWeapon(next);
	return next;
}

export function planCpuTurn(state: ScorchedEarthState): CpuTurnDecision {
	const player = state.players[state.currentPlayerIndex];
	if (!player?.alive || player.kind !== 'cpu') {
		return { action: 'pass' };
	}
	if (cpuShouldPass(state, player)) {
		return { action: 'pass' };
	}
	const weaponId = cpuChooseWeapon(state, player);
	if (player.inventory[weaponId] <= 0) {
		return { action: 'pass' };
	}
	const aim = cpuComputeAim(state, player, weaponId);
	return { action: 'fire', aim: { ...aim, weaponId } };
}

function pickPrimaryTarget(shooter: Tank, enemies: Tank[]): Tank {
	return enemies
		.slice()
		.sort((a, b) => {
			const da = distance({ x: shooter.x, y: shooter.y }, { x: a.x, y: a.y });
			const db = distance({ x: shooter.x, y: shooter.y }, { x: b.x, y: b.y });
			const scoreA = a.health * 0.6 + da * 0.4;
			const scoreB = b.health * 0.6 + db * 0.4;
			return scoreA - scoreB;
		})[0];
}

function pickFirstAvailable(player: Tank): WeaponId {
	for (const id of WEAPON_ORDER) {
		if (player.inventory[id] > 0) {
			return id;
		}
	}
	return 'babyMissile';
}

function simulateImpact(opts: {
	angle: number;
	power: number;
	startX: number;
	startY: number;
	facingRight: boolean;
	weaponId: WeaponId;
	wind: number;
	terrain: Float32Array;
}): { x: number; y: number } | null {
	const vel = launchVelocity(opts.angle, opts.power, opts.facingRight, opts.weaponId);
	let projectile: Projectile = {
		id: 0,
		x: opts.startX,
		y: opts.startY,
		vx: vel.x,
		vy: vel.y,
		ownerId: -1,
		weaponId: opts.weaponId,
		age: 0,
		splitDone: opts.weaponId !== 'mirv',
		trail: [],
	};

	for (let step = 0; step < 700; step++) {
		projectile = stepProjectile(projectile, opts.terrain, opts.wind);
		if (
			projectileHitsTerrain(projectile, opts.terrain) ||
			projectileOutOfBounds(projectile, WORLD_WIDTH, WORLD_HEIGHT)
		) {
			return { x: projectile.x, y: projectile.y };
		}
	}
	return null;
}

function seededUnit(seed: number): number {
	const x = Math.sin(seed * 0.00097 + 91.7) * 43758.5453;
	return x - Math.floor(x);
}

function clamp(v: number, min: number, max: number): number {
	return Math.max(min, Math.min(max, v));
}
